import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import ErrorToast from '../components/ErrorToast';
import Paginacao from '../components/Paginacao';
import FiltroClientes from '../components/FiltroClientes'; // Import the new component
import '../styles/Common.css';
import './Clientes.css';
import { PlusCircle, Tags } from 'lucide-react';
import BulkEditEtiquetasModal from '../components/BulkEditEtiquetasModal';

const Clientes = () => {
  const [filtros, setFiltros] = useState({
    id: '',
    nome: '',
    documento: '',
    tipo: '',
    contribuinte_origem: '',
    apenas_proprietarios: false,
  });
  const [clientes, setClientes] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [tamanho] = useState(10);
  const [totalItens, setTotalItens] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [modalEtiquetasAberto, setModalEtiquetasAberto] = useState(false);
  const [selecionados, setSelecionados] = useState([]);
  const [novoCliente, setNovoCliente] = useState({
    nome: '',
    documento: '',
    tipo: '',
    contribuinte_origem: ''
  });

  const buscarClientes = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const activeFilters = Object.entries(filtros).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {});

      const resposta = await api.get(`/clientes`, {
        params: { ...activeFilters, page: pagina, pageSize: tamanho }
      });
      setClientes(resposta.data.data || []);
      setTotalItens(resposta.data.total || 0);
    } catch (e) {
      const msg = e.response?.data?.error || 'Erro ao buscar clientes.';
      setErro(msg);
    } finally {
      setCarregando(false);
    }
  }, [filtros, pagina, tamanho]);

  useEffect(() => {
    buscarClientes();
  }, [buscarClientes]);

  const allIdsOnPage = useMemo(() => (Array.isArray(clientes) ? clientes.map(c => c.id) : []), [clientes]);
  const allOnPageSelected = useMemo(() => allIdsOnPage.length > 0 && allIdsOnPage.every(id => selecionados.includes(id)), [allIdsOnPage, selecionados]);
  const toggleSelectAllOnPage = () => {
    setSelecionados(prev => {
      if (allOnPageSelected) {
        return prev.filter(id => !allIdsOnPage.includes(id));
      }
      const set = new Set(prev);
      allIdsOnPage.forEach(id => set.add(id));
      return Array.from(set);
    });
  };
  const toggleSelect = (id) => {
    setSelecionados(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  // New handler for the filter component
  const handleSearch = (newFilters) => {
    setPagina(1);
    setFiltros(newFilters);
  };

  const handleNovoInput = e => {
    const { name, value } = e.target;
    setNovoCliente(prev => ({ ...prev, [name]: value }));
  };

  const handleNovoSubmit = async e => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/clientes`, novoCliente);
      setClientes(prev => [...prev, data]);
      setToastMsg('Cliente cadastrado com sucesso.');
      setModalAberto(false);
      setNovoCliente({
        nome: '',
        documento: '',
        tipo: '',
        contribuinte_origem: ''
      });
    } catch (e) {
      const msg = e.response?.data?.error || 'Erro ao cadastrar cliente.';
      setErro(msg);
    }
  };

  return (
    <div className="page-container clientes-container">
      <h1 className="page-title">Clientes</h1>

      {/* Replace the old form with the new component */}
      <FiltroClientes onSearch={handleSearch} />

      {erro && <ErrorToast message={erro} onClose={() => setErro('')} />}
      {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg('')} />}

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
        <button
          className="button-primary"
          onClick={() => setModalAberto(true)}
          style={{ alignSelf: 'flex-start' }}
        >
          <PlusCircle size={16} />
          <span>Adicionar Cliente</span>
        </button>
        <button
          className="button-secondary"
          onClick={() => setModalEtiquetasAberto(true)}
          title="Editar Etiquetas em Massa"
          disabled={selecionados.length === 0}
        >
          <Tags size={16} />
          <span style={{ marginLeft: 6 }}>Editar Etiquetas ({selecionados.length})</span>
        </button>
      </div>

      {carregando ? (
        <Loading />
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    aria-label="Selecionar todos da página"
                  />
                </th>
                <th>Detalhes do Cliente</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(clientes) && clientes.length > 0 ? (
                clientes.map(cliente => (
                  <tr key={cliente.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selecionados.includes(cliente.id)}
                        onChange={() => toggleSelect(cliente.id)}
                        aria-label={`Selecionar cliente ${cliente.id}`}
                      />
                    </td>
                    <td>
                      <div className="cliente-details">
                        <Link to={`/clientes/${cliente.id}`} className="cliente-link">
                          {cliente.nome}
                        </Link>
                        <div className="cliente-meta">
                          <span><strong>ID:</strong> {cliente.id}</span>
                          <span><strong>Documento:</strong> {cliente.documento}</span>
                          <span><strong>Tipo:</strong> {cliente.tipo}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="1" className="text-center">Nenhum cliente encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Paginacao
        totalItens={totalItens}
        itensPorPagina={tamanho}
        paginaAtual={pagina}
        setPaginaAtual={setPagina}
      />

      {modalAberto && (
        <div className="modal-overlay" onClick={() => setModalAberto(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setModalAberto(false)}
              className="modal-close"
            >
              ×
            </button>
            <h2 className="page-title" style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>
              Novo Cliente
            </h2>
            <form className="form-cliente" onSubmit={handleNovoSubmit}>
              <div className="form-row">
                <label>Nome:</label>
                <input
                  type="text"
                  name="nome"
                  value={novoCliente.nome}
                  onChange={handleNovoInput}
                  required
                />
              </div>
              <div className="form-row">
                <label>Documento:</label>
                <input
                  type="text"
                  name="documento"
                  value={novoCliente.documento}
                  onChange={handleNovoInput}
                />
              </div>
              <div className="form-row">
                <label>Tipo:</label>
                <input
                  type="text"
                  name="tipo"
                  value={novoCliente.tipo}
                  onChange={handleNovoInput}
                  placeholder="PF ou PJ"
                />
              </div>
              <div className="form-row">
                <label>Origem:</label>
                <input
                  type="text"
                  name="contribuinte_origem"
                  value={novoCliente.contribuinte_origem}
                  onChange={handleNovoInput}
                />
              </div>
              <button type="submit" className="button-primary">
                Cadastrar
              </button>
            </form>
          </div>
        </div>
      )}

      <BulkEditEtiquetasModal
        isOpen={modalEtiquetasAberto}
        onClose={() => setModalEtiquetasAberto(false)}
        selectedIds={selecionados}
        type="clientes"
        onSuccess={() => {
          setToastMsg('Etiquetas atualizadas com sucesso!');
          setSelecionados([]);
          buscarClientes();
        }}
      />
    </div>
  );
};

export default Clientes;
